import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import type { TooltipContentProps } from 'recharts'
import type { DailyRevenueItem } from '../api/admin-api'

interface DailyRevenueChartProps {
  data: DailyRevenueItem[]
}

function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr)
  const dayNames = ['일', '월', '화', '수', '목', '금', '토']
  return `${date.getMonth() + 1}/${date.getDate()} (${dayNames[date.getDay()]})`
}

function formatKrw(value: number): string {
  return value.toLocaleString('ko-KR')
}

function formatKrwAbbrev(value: number): string {
  if (value >= 10_000) {
    const man = value / 10_000
    return `${man % 1 === 0 ? man.toFixed(0) : man.toFixed(1)}만`
  }
  if (value >= 1_000) {
    const chun = value / 1_000
    return `${chun % 1 === 0 ? chun.toFixed(0) : chun.toFixed(1)}천`
  }
  return String(value)
}

function CustomTooltip({ active, payload, label }: Partial<TooltipContentProps<number, string>>) {
  if (!active || !payload?.length) return null

  const costKrw = payload.find((p) => p.dataKey === 'costKrw')?.value
  const profitKrw = payload.find((p) => p.dataKey === 'profitKrw')?.value
  const cost = typeof costKrw === 'number' ? costKrw : 0
  const profit = typeof profitKrw === 'number' ? profitKrw : 0
  const revenue = cost + profit

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium text-gray-900 mb-2">
        {formatDateLabel(String(label))}
      </p>
      <div className="space-y-1">
        <p className="text-sm text-gray-900">
          매출: <span className="font-semibold">{formatKrw(revenue)}</span>원
        </p>
        <p className="text-sm text-amber-600">
          원가: <span className="font-semibold">{formatKrw(cost)}</span>원
        </p>
        <p className="text-sm text-blue-600">
          차익: <span className="font-semibold">{formatKrw(profit)}</span>원
        </p>
      </div>
    </div>
  )
}

export function DailyRevenueChart({ data }: DailyRevenueChartProps) {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={(value) => {
            const d = new Date(value)
            return `${d.getMonth() + 1}/${d.getDate()}`
          }}
          tick={{ fontSize: 11, fill: '#64748B' }}
          axisLine={{ stroke: '#E2E8F0' }}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#64748B' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={formatKrwAbbrev}
          width={50}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F1F5F9' }} />
        <Bar
          dataKey="costKrw"
          stackId="revenue"
          fill="#F59E0B"
          radius={[0, 0, 0, 0]}
          maxBarSize={32}
        />
        <Bar
          dataKey="profitKrw"
          stackId="revenue"
          fill="#3B82F6"
          radius={[4, 4, 0, 0]}
          maxBarSize={32}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}
